/**
 * EditorBanner — WYSIWYG solution text banner.
 *
 * Width matches the available text area inside the game's solution rectangle
 * (within the circle border, plus margins). Uses the same font size and weight
 * as the game's solution display (37px/800wt normal, 30px/800wt smaller).
 *
 * Default: SVG preview showing exact game rendering.
 * Click to edit: input overlay for typing.
 * Shows a dot indicator when using the smaller font size.
 */

import React, { useRef, useMemo, useState, useCallback } from 'react';
import { fitSolutionText } from '../../utils/textFitting';

interface EditorBannerProps {
  solution: string;
  onChange: (text: string) => void;
}

// Available text width inside the solution rectangle's circle border + margin.
// Game SVG viewBox: 538.499px. Circle inner edges: ~13.4px from each side.
// SVG scaled to 718px container → scale 1.333. Inner circle at ~17.9px screen.
// With ~20px inner margin → text area ~38px to ~680px = ~642px.
const BANNER_WIDTH = 640;
const BANNER_HEIGHT = 96;
// Padding inside for fitting calculation (small margin so text doesn't touch edges)
const TEXT_PADDING = 24;

const EditorBanner: React.FC<EditorBannerProps> = ({ solution, onChange }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [editing, setEditing] = useState(false);

  const fit = useMemo(
    () => fitSolutionText(solution || 'X', BANNER_WIDTH - TEXT_PADDING),
    [solution]
  );

  const isEmpty = !solution;
  const lines = isEmpty ? [] : fit.lines;

  const handleClick = useCallback(() => {
    setEditing(true);
    requestAnimationFrame(() => inputRef.current?.focus());
  }, []);

  const handleBlur = useCallback(() => {
    setEditing(false);
  }, []);

  return (
    <div
      onClick={!editing ? handleClick : undefined}
      style={{
        width: BANNER_WIDTH,
        minWidth: BANNER_WIDTH,
        maxWidth: BANNER_WIDTH,
        height: BANNER_HEIGHT,
        background: '#387c47',
        borderRadius: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'text',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* SVG preview (default) */}
      {!editing && (
        isEmpty ? (
          <span style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: 18,
            fontWeight: 600,
            color: '#f7e5c5',
            opacity: 0.2,
            textTransform: 'uppercase',
            pointerEvents: 'none',
          }}>
            SOLUTION
          </span>
        ) : (
          <svg
            width={BANNER_WIDTH}
            height={BANNER_HEIGHT}
            viewBox={`0 0 ${BANNER_WIDTH} ${BANNER_HEIGHT}`}
            style={{ pointerEvents: 'none' }}
          >
            <text
              x="50%"
              y="50%"
              dy=".1em"
              fill="#f7e5c5"
              fontFamily="Inter, sans-serif"
              fontSize={fit.fontSize}
              fontWeight="800"
              textAnchor="middle"
              dominantBaseline="middle"
            >
              {lines[0] ?? ''}
            </text>
          </svg>
        )
      )}

      {/* Edit input overlay */}
      {editing && (
        <input
          ref={inputRef}
          value={solution}
          onChange={e => onChange(e.target.value)}
          onBlur={handleBlur}
          placeholder="SOLUTION"
          maxLength={80}
          style={{
            background: 'transparent',
            border: 'none',
            outline: 'none',
            textAlign: 'center',
            fontFamily: 'Inter, sans-serif',
            fontSize: 22,
            fontWeight: 800,
            color: '#f7e5c5',
            textTransform: 'uppercase',
            width: '90%',
            padding: 0,
          }}
        />
      )}

      {/* Smaller-font dot indicator */}
      {!isEmpty && fit.isSmaller && !fit.needsManualFix && (
        <div
          title={`Using smaller font (${fit.fontSize}px)`}
          style={{
            position: 'absolute',
            top: 6,
            right: 8,
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: '#e6b832',
            opacity: 0.6,
          }}
        />
      )}

      {/* Overflow warning dot */}
      {!isEmpty && fit.needsManualFix && (
        <div
          title="Text too long for solution area"
          style={{
            position: 'absolute',
            top: 6,
            right: 8,
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: '#e05c2a',
          }}
        />
      )}
    </div>
  );
};

export default React.memo(EditorBanner);
